import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthActor, AuthSession } from './types';

type AuthContextValue = Readonly<{
  session: AuthSession | null;
  loading: boolean;
  companyBrainUrl: string;
  openCompanyBrain(): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
}>;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const LOCAL_SESSION_KEY = 'echo-crm-access-token';
type AuthProviderKind = 'local' | 'supabase';

/** Opening Company Brain must never hang: it either navigates or reports why. */
export const COMPANY_BRAIN_TIMEOUT_MS = 8_000;

export class CompanyBrainError extends Error {
  readonly reason: string;

  constructor(message: string, reason: string, cause?: unknown) {
    super(message);
    this.name = 'CompanyBrainError';
    this.reason = reason;
    console.error(JSON.stringify({
      event: 'company_brain.open_failed',
      reason,
      detail: cause instanceof Error ? cause.message : cause ?? null,
    }));
  }
}

function browserDemoSession(): AuthSession | null {
  if (typeof window === 'undefined' || new URLSearchParams(window.location.search).get('demo') !== '1') return null;
  return {
    accessToken: 'echo-crm-demo-session',
    actor: {
      id: '10000000-0000-4000-8000-000000000001',
      role: 'admin',
      name: 'Анна Волкова',
      email: 'admin@goecho.ai',
    },
  };
}

function createSupabase(url?: string, anonKey?: string): SupabaseClient | undefined {
  return url && anonKey ? createClient(url, anonKey, { auth: { persistSession: true } }) : undefined;
}

async function loadActor(accessToken: string): Promise<AuthActor> {
  const response = await fetch('/api/v1/me', {
    headers: { authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) throw new Error('Не удалось загрузить профиль CRM');
  return response.json() as Promise<AuthActor>;
}

export function AuthProvider(input: {
  children: ReactNode;
  initialSession?: AuthSession | null;
}): ReactNode {
  const buildSupabase = useMemo(() => createSupabase(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
  ), []);
  const demoSession = useMemo(
    () => input.initialSession === undefined ? browserDemoSession() : null,
    [input.initialSession],
  );
  const [supabase, setSupabase] = useState<SupabaseClient | undefined>(buildSupabase);
  const [authProvider, setAuthProvider] = useState<AuthProviderKind>(buildSupabase ? 'supabase' : 'local');
  const [companyBrainUrl, setCompanyBrainUrl] = useState(
    import.meta.env.VITE_COMPANY_BRAIN_URL ?? 'https://brain.goecho.ai',
  );
  const [clientConfigResolved, setClientConfigResolved] = useState(
    Boolean(buildSupabase || demoSession || input.initialSession !== undefined),
  );
  const [session, setSession] = useState<AuthSession | null>(input.initialSession ?? demoSession);
  const [loading, setLoading] = useState(input.initialSession === undefined && !demoSession);

  useEffect(() => {
    if (clientConfigResolved) return undefined;
    const controller = new AbortController();
    void fetch('/api/v1/client-config', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Client configuration is unavailable');
        return response.json() as Promise<{
          authProvider?: AuthProviderKind;
          supabaseUrl?: string | null;
          supabaseAnonKey?: string | null;
          companyBrainUrl?: string | null;
        }>;
      })
      .then((config) => {
        setAuthProvider(config.authProvider ?? (config.supabaseUrl ? 'supabase' : 'local'));
        setSupabase(createSupabase(config.supabaseUrl ?? undefined, config.supabaseAnonKey ?? undefined));
        if (config.companyBrainUrl) setCompanyBrainUrl(config.companyBrainUrl);
      })
      .catch(() => setSupabase(undefined))
      .finally(() => setClientConfigResolved(true));
    return () => controller.abort();
  }, [clientConfigResolved]);

  useEffect(() => {
    if (input.initialSession !== undefined || demoSession) return undefined;
    if (!clientConfigResolved) return undefined;
    if (authProvider === 'local') {
      const accessToken = window.sessionStorage.getItem(LOCAL_SESSION_KEY);
      if (!accessToken) {
        setLoading(false);
        return undefined;
      }
      let active = true;
      void loadActor(accessToken)
        .then((actor) => {
          if (active) setSession({ accessToken, actor });
        })
        .catch(() => window.sessionStorage.removeItem(LOCAL_SESSION_KEY))
        .finally(() => {
          if (active) setLoading(false);
        });
      return () => { active = false; };
    }
    if (!supabase) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    void supabase.auth.getSession().then(async ({ data }) => {
      const accessToken = data.session?.access_token;
      if (!active) return;
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        setSession({ accessToken, actor: await loadActor(accessToken) });
      } finally {
        if (active) setLoading(false);
      }
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!next?.access_token) setSession(null);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [authProvider, clientConfigResolved, demoSession, input.initialSession, supabase]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    loading,
    companyBrainUrl,
    async openCompanyBrain() {
      if (!session?.accessToken) throw new CompanyBrainError('Сначала войдите в CRM', 'no_session');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), COMPANY_BRAIN_TIMEOUT_MS);
      let response: Response;
      try {
        response = await fetch('/api/v1/auth/company-brain', {
          headers: { authorization: `Bearer ${session.accessToken}` },
          signal: controller.signal,
        });
      } catch (error) {
        const aborted = controller.signal.aborted;
        throw new CompanyBrainError(
          aborted
            ? `Company Brain не ответил за ${COMPANY_BRAIN_TIMEOUT_MS / 1000} секунд`
            : 'Company Brain недоступен — проверьте сеть',
          aborted ? 'timeout' : 'network_error',
          error,
        );
      } finally {
        clearTimeout(timeout);
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => undefined) as
          { error?: { code?: string; message?: string; requestId?: string } } | undefined;
        throw new CompanyBrainError(
          payload?.error?.code === 'provider_unavailable'
            ? 'Company Brain SSO не настроен на сервере'
            : `Company Brain вернул ошибку ${response.status}`,
          payload?.error?.code ?? `http_${response.status}`,
          payload?.error,
        );
      }
      const payload = await response.json().catch(() => undefined) as { redirectUrl?: string } | undefined;
      if (!payload?.redirectUrl) {
        throw new CompanyBrainError('Company Brain SSO не настроен', 'missing_redirect_url');
      }
      window.location.assign(payload.redirectUrl);
    },
    async signIn(email, password) {
      if (authProvider === 'local') {
        const response = await fetch('/api/v1/auth/login', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        if (!response.ok) throw new Error('Неверный email или пароль');
        const payload = await response.json() as { accessToken: string };
        window.sessionStorage.setItem(LOCAL_SESSION_KEY, payload.accessToken);
        setSession({ accessToken: payload.accessToken, actor: await loadActor(payload.accessToken) });
        return;
      }
      if (!supabase) throw new Error('Supabase Auth не настроен');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error || !data.session) throw error ?? new Error('Не удалось войти');
      setSession({ accessToken: data.session.access_token, actor: await loadActor(data.session.access_token) });
    },
    async signOut() {
      window.sessionStorage.removeItem(LOCAL_SESSION_KEY);
      await supabase?.auth.signOut();
      setSession(null);
    },
    async changePassword(currentPassword, newPassword) {
      if (authProvider === 'local') {
        const response = await fetch('/api/v1/auth/change-password', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(session?.accessToken ? { authorization: `Bearer ${session.accessToken}` } : {}),
          },
          body: JSON.stringify({ currentPassword, newPassword }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => undefined) as { error?: { message?: string } } | undefined;
          throw new Error(payload?.error?.message ?? 'Не удалось сменить пароль');
        }
        return;
      }
      if (!supabase) throw new Error('Supabase Auth не настроен');
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    },
    async requestPasswordReset(email) {
      if (authProvider === 'local') {
        throw new Error('Для сброса пароля обратитесь к администратору CRM');
      }
      if (!supabase) throw new Error('Supabase Auth не настроен');
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    },
  }), [authProvider, companyBrainUrl, loading, session, supabase]);

  return <AuthContext.Provider value={value}>{input.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
